import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Locale } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import newRequest, { buildBaseURL } from "../../utils/http/newRuequest";
import Base from "../Base";

const { Paragraph, Text } = Typography;
const i18n = Locale.getInstance();

const API = { name: "profit sharing reconciliation" };

const SCOPE_OPTIONS = [
  { value: "flagged", labelKey: "scopes.flagged" },
  { value: "pending", labelKey: "scopes.pending" },
  { value: "in_progress", labelKey: "scopes.inProgress" },
  { value: "stale_zero", labelKey: "scopes.staleZero" },
  { value: "all", labelKey: "scopes.all" },
];

const SEVERITY_COLORS = {
  high: "red",
  medium: "orange",
  none: "default",
};

const ACTION_LABELS = {
  sync_from_wechat: "actions.syncFromWechat",
  sync_refunded_cleanup: "actions.syncRefundedCleanup",
  request_finish: "actions.requestFinish",
  resolve_sharing_rounds: "actions.resolveSharingRounds",
  inspect_order_debug: "actions.inspectOrderDebug",
  none: "actions.none",
};

const ISSUE_LABELS = {
  db_unsynced: "issues.dbUnsynced",
  db_pending: "issues.dbPending",
  refunded_stale_unfinished: "issues.refundedStaleUnfinished",
  sharing_open: "issues.sharingOpen",
  sharing_missing: "issues.sharingMissing",
  finish_missing: "issues.finishMissing",
  finish_open: "issues.finishOpen",
  finish_blocked: "issues.finishBlocked",
  order_in_progress: "issues.orderInProgress",
};

const ORDER_STATUS_COMPLETED = 4;

function canRequestFinish(row) {
  return !row.refund?.isRefunded && Number(row.status) === ORDER_STATUS_COMPLETED;
}

const TERMINAL_SHARING_STATUSES = new Set(["FINISHED", "CLOSED", "FAILED"]);

function fenToYuan(fen) {
  if (fen === null || fen === undefined || fen === "") return "—";
  return `${(Number(fen) / 100).toFixed(2)} ${i18n.t("common.yuan")}`;
}

function SummaryStatistics({ summary }) {
  const realPending = summary?.reconciliation?.realPendingAmount;
  const residualGap = summary?.reconciliation?.residualGap;
  const pendingGap = summary?.reconciliation?.pendingGap;

  const renderMoney = (fen, highlightMode = false) => {
    const value = fenToYuan(fen);
    if (fen === null || fen === undefined || fen === "") {
      return value;
    }
    const amount = Number(fen);
    if (highlightMode === true && amount > 100) {
      return <Text strong style={{ color: "#cf1322" }}>{value}</Text>;
    }
    if (highlightMode === "abs" && Math.abs(amount) > 100) {
      return <Text strong style={{ color: "#d48806" }}>{value}</Text>;
    }
    return <Text strong>{value}</Text>;
  };

  const stat = (labelKey) => i18n.t(`admin.profitSharingReconciliation.stats.${labelKey}`);

  return (
    <Card
      size="small"
      title={i18n.t("admin.profitSharingReconciliation.statsTitle")}
      style={{ marginBottom: 16 }}
    >
      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label={stat("pendingWechat")}>
          {renderMoney(summary.balance?.pendingAmount)}
        </Descriptions.Item>
        <Descriptions.Item label={stat("realPending")}>
          {renderMoney(realPending, true)}
        </Descriptions.Item>
        <Descriptions.Item label={stat("inProgressUnfinished")}>
          {renderMoney(summary.dbTotals?.sumInProgressUnfinished)}
        </Descriptions.Item>
        <Descriptions.Item label={stat("residualGap")}>
          {renderMoney(residualGap, "abs")}
        </Descriptions.Item>
        <Descriptions.Item label={stat("availableWechat")}>
          {renderMoney(summary.balance?.availableAmount)}
        </Descriptions.Item>
        <Descriptions.Item label={stat("dbSumUnfinished")}>
          {renderMoney(summary.dbTotals?.sumUnfinishedAmount)}
        </Descriptions.Item>
        <Descriptions.Item label={stat("ordersPending")}>
          <Text strong>{summary.dbTotals?.countPending ?? 0}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={stat("pendingGap")}>
          {renderMoney(pendingGap, "abs")}
        </Descriptions.Item>
        <Descriptions.Item label={stat("nullUnfinished")}>
          <Text strong>{summary.dbTotals?.countNullUnfinished ?? 0}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={stat("staleZero")}>
          <Text strong>{summary.dbTotals?.countStaleZero ?? 0}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={stat("inProgressOrders")}>
          <Text strong>{summary.dbTotals?.countInProgress ?? 0}</Text>
        </Descriptions.Item>
      </Descriptions>
      {summary.balance?.balanceUpdatedAt && (
        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          {i18n.t("admin.profitSharingReconciliation.balanceUpdatedAt", {
            time: new Date(summary.balance.balanceUpdatedAt).toLocaleString(),
          })}
        </Paragraph>
      )}
      <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
        {i18n.t("admin.profitSharingReconciliation.stats.realPendingHint")}
      </Paragraph>
    </Card>
  );
}

function buildUrl(path, params = {}) {
  const baseUrl = buildBaseURL("v6", "");
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  });
  const q = qs.toString();
  return `${baseUrl}merchant/${path}${q ? `?${q}` : ""}`;
}

async function fetchSummary(token, refreshBalance) {
  const url = buildUrl("admin/profit-sharing-reconciliation", {
    refresh_balance: refreshBalance ? "1" : "0",
  });
  const res = await newRequest.get(url, token);
  if (!res?.result) throw new Error(res?.message || "fetch failed");
  return res.data;
}

async function fetchOrders(token, { scope, page, perPage, orderId }) {
  const url = buildUrl("admin/profit-sharing-reconciliation/orders", {
    scope,
    page,
    per_page: perPage,
    order_id: orderId || undefined,
  });
  const res = await newRequest.get(url, token);
  if (!res?.result) throw new Error(res?.message || "fetch failed");
  return res.data;
}

async function postBulkSync(token, scope) {
  const url = buildUrl("admin/profit-sharing-reconciliation/orders/sync-pending", {
    scope,
  });
  const res = await newRequest.post(url, {}, token);
  if (!res?.result) throw new Error(res?.message || "bulk sync failed");
  return res.data;
}

async function postBulkFinish(token, scope) {
  const url = buildUrl(
    "admin/profit-sharing-reconciliation/orders/request-finish-pending",
    { scope }
  );
  const res = await newRequest.post(url, {}, token);
  if (!res?.result) throw new Error(res?.message || "bulk finish failed");
  return res.data;
}

async function postAction(token, orderId, action) {
  const path =
    action === "sync"
      ? `admin/profit-sharing-reconciliation/orders/${orderId}/sync`
      : `admin/profit-sharing-reconciliation/orders/${orderId}/request-finish`;
  const url = buildUrl(path);
  const res = await newRequest.post(url, {}, token);
  if (!res?.result) throw new Error(res?.message || "action failed");
  return res.data;
}

function BulkActionResultsModal({ open, data, onClose, i18nKey }) {
  if (!data) return null;

  const t = (key, params) =>
    i18n.t(`admin.profitSharingReconciliation.${i18nKey}.${key}`, params);

  const columns = [
    {
      title: i18n.t("admin.profitSharingReconciliation.columns.orderId"),
      dataIndex: "orderId",
      width: 100,
    },
    {
      title: t("result"),
      dataIndex: "success",
      width: 100,
      render: (success) =>
        success ? (
          <Tag color="green">{i18n.t("admin.orderDebug.checkPassed")}</Tag>
        ) : (
          <Tag color="red">{i18n.t("admin.orderDebug.checkFailed")}</Tag>
        ),
    },
    {
      title: t("detail"),
      key: "detail",
      render: (_, row) =>
        row.success ? (
          <Text type="secondary">{t("done")}</Text>
        ) : (
          <Text type="danger">{row.error || "—"}</Text>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      title={t("resultsTitle")}
      onCancel={onClose}
      onOk={onClose}
      width={720}
      destroyOnClose
    >
      <Alert
        type={data.failedCount > 0 ? "warning" : "success"}
        showIcon
        style={{ marginBottom: 16 }}
        message={t("summary", {
          total: data.total,
          success: data.successCount,
          failed: data.failedCount,
        })}
      />
      <Table
        rowKey="orderId"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={data.results || []}
        scroll={{ y: 360 }}
      />
    </Modal>
  );
}

function IssueTags({ issues }) {
  if (!issues?.length) return <Tag color="green">{i18n.t("admin.profitSharingReconciliation.noIssues")}</Tag>;
  return (
    <Space size={[4, 4]} wrap>
      {issues.map((issue, idx) => {
        const labelKey = ISSUE_LABELS[issue.code];
        const label = labelKey ? i18n.t(`admin.profitSharingReconciliation.${labelKey}`) : issue.code;
        return (
          <Tooltip key={`${issue.code}-${idx}`} title={issue.message}>
            <Tag color={SEVERITY_COLORS[issue.severity] || "default"}>{label}</Tag>
          </Tooltip>
        );
      })}
    </Space>
  );
}

function RefundCell({ refund }) {
  if (!refund?.hasRefund) return <Text type="secondary">—</Text>;
  if (refund.isRefunded) return <Tag color="orange">{i18n.t("admin.profitSharingReconciliation.refundRefunded")}</Tag>;
  return <Tag>{i18n.t("admin.profitSharingReconciliation.refundInProgress")}</Tag>;
}

function isTerminalSharingRound(round) {
  if (TERMINAL_SHARING_STATUSES.has(round.status)) return true;
  return false;
}

function SharingRoundsCell({ rounds }) {
  if (!rounds?.length) return <Text type="secondary">—</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {rounds.map((r) => {
        const terminal = isTerminalSharingRound(r);
        const label = r.closeReason ? `${r.typeLabel}:${r.status}(${r.closeReason})` : `${r.typeLabel}:${r.status || "?"}`;
        return (
          <Tag key={r.id} color={terminal ? "green" : "blue"}>
            {label}
          </Tag>
        );
      })}
    </Space>
  );
}

function CopyableCommand({ command }) {
  return (
    <Paragraph
      copyable={{ text: command }}
      code
      style={{ marginBottom: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}
    >
      {command}
    </Paragraph>
  );
}

function PendingGapPlaybook({ summary, merchantId, onSwitchStaleZeroScope }) {
  const pendingAmount = Number(summary?.balance?.pendingAmount ?? 0);
  const dbSum = Number(summary?.dbTotals?.sumUnfinishedAmount ?? 0);
  const inProgressSum = Number(summary?.dbTotals?.sumInProgressUnfinished ?? 0);
  const realPending = Number(summary?.reconciliation?.realPendingAmount ?? pendingAmount - inProgressSum);
  const countPending = Number(summary?.dbTotals?.countPending ?? 0);
  const countNull = Number(summary?.dbTotals?.countNullUnfinished ?? 0);
  const gap = summary?.reconciliation?.pendingGap;
  const merchantFlag = merchantId ? `--merchant=${merchantId}` : "--merchant=MERCHANT_ID";

  const hasStaleZeroGap =
    realPending > 100 &&
    dbSum === 0 &&
    countPending === 0 &&
    countNull === 0;

  const cmdFind = `php artisan profit-sharing:sum-unsplit-by-merchant ${merchantFlag} --refresh-balance --all-time`;
  const cmdFindBatch = `php artisan profit-sharing:sum-unsplit-by-merchant ${merchantFlag} --all-time --txn-limit=200 --txn-offset=0`;
  const cmdSyncDb = `php artisan profit-sharing:sum-unsplit-by-merchant ${merchantFlag} --all-time --sync-db --no-finish`;
  const cmdSyncAndFinish = `php artisan profit-sharing:sum-unsplit-by-merchant ${merchantFlag} --all-time --sync-db`;
  const cmdRefundCleanup = `php artisan wechat-profit-sharing:sync-refunded-order ${merchantFlag}`;

  const stepItems = [
    {
      title: i18n.t("admin.profitSharingReconciliation.playbook.steps.confirm.title"),
      description: (
        <Space direction="vertical" size="small">
          <Text>{i18n.t("admin.profitSharingReconciliation.playbook.steps.confirm.description")}</Text>
          {hasStaleZeroGap && (
            <Alert
              type="info"
              showIcon
              message={i18n.t("admin.profitSharingReconciliation.playbook.staleZeroHintTitle")}
              description={i18n.t("admin.profitSharingReconciliation.playbook.staleZeroHintDescription")}
            />
          )}
        </Space>
      ),
    },
    {
      title: i18n.t("admin.profitSharingReconciliation.playbook.steps.find.title"),
      description: (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text>{i18n.t("admin.profitSharingReconciliation.playbook.steps.find.description")}</Text>
          <CopyableCommand command={cmdFind} />
          <Text type="secondary">{i18n.t("admin.profitSharingReconciliation.playbook.steps.find.batchNote")}</Text>
          <CopyableCommand command={cmdFindBatch} />
        </Space>
      ),
    },
    {
      title: i18n.t("admin.profitSharingReconciliation.playbook.steps.syncDb.title"),
      description: (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text>{i18n.t("admin.profitSharingReconciliation.playbook.steps.syncDb.description")}</Text>
          <CopyableCommand command={cmdSyncDb} />
          <Button size="small" onClick={onSwitchStaleZeroScope}>
            {i18n.t("admin.profitSharingReconciliation.playbook.switchStaleZeroScope")}
          </Button>
        </Space>
      ),
    },
    {
      title: i18n.t("admin.profitSharingReconciliation.playbook.steps.finish.title"),
      description: (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text>{i18n.t("admin.profitSharingReconciliation.playbook.steps.finish.description")}</Text>
          <CopyableCommand command={cmdSyncAndFinish} />
        </Space>
      ),
    },
    {
      title: i18n.t("admin.profitSharingReconciliation.playbook.steps.refund.title"),
      description: (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text>{i18n.t("admin.profitSharingReconciliation.playbook.steps.refund.description")}</Text>
          <CopyableCommand command={cmdRefundCleanup} />
        </Space>
      ),
    },
    {
      title: i18n.t("admin.profitSharingReconciliation.playbook.steps.interpret.title"),
      description: (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>{i18n.t("admin.profitSharingReconciliation.playbook.steps.interpret.match")}</li>
          <li>{i18n.t("admin.profitSharingReconciliation.playbook.steps.interpret.less")}</li>
          <li>{i18n.t("admin.profitSharingReconciliation.playbook.steps.interpret.zero")}</li>
        </ul>
      ),
    },
  ];

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      defaultActiveKey={hasStaleZeroGap || realPending > 100 ? ["playbook"] : []}
      items={[
        {
          key: "playbook",
          label: (
            <Space>
              <Text strong>{i18n.t("admin.profitSharingReconciliation.playbook.title")}</Text>
              {hasStaleZeroGap && (
                <Tag color="orange">{i18n.t("admin.profitSharingReconciliation.playbook.recommended")}</Tag>
              )}
            </Space>
          ),
          children: (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {i18n.t("admin.profitSharingReconciliation.playbook.intro")}
              </Paragraph>
              <Steps direction="vertical" size="small" current={-1} items={stepItems} />
            </Space>
          ),
        },
      ]}
    />
  );
}

const ProfitSharingReconciliation = () => {
  const user = useSelector((state) => getUserInfo(state));
  const base = Base.getInstance();
  const queryClient = useQueryClient();

  const [scope, setScope] = useState("flagged");
  const [page, setPage] = useState(1);
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [appliedOrderId, setAppliedOrderId] = useState("");
  const [bulkSyncResult, setBulkSyncResult] = useState(null);
  const [bulkFinishResult, setBulkFinishResult] = useState(null);

  const merchantLabel = user?.merchant
    ? `#${user.merchant.id} ${user.merchant.zh_name || user.merchant.ug_name || ""}`
    : "";

  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["profitSharingReconciliationSummary"],
    queryFn: () => fetchSummary(user?.token, false),
    enabled: !!user?.token,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (err) => base.handleApiError(API, err),
  });

  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ["profitSharingReconciliationOrders", scope, page, appliedOrderId],
    queryFn: () =>
      fetchOrders(user?.token, {
        scope,
        page,
        perPage: 20,
        orderId: appliedOrderId,
      }),
    enabled: !!user?.token,
    keepPreviousData: true,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (err) => base.handleApiError(API, err),
  });

  const handleRefresh = () => {
    refetchSummary();
    refetchOrders();
  };

  const refreshBalanceMutation = useMutation({
    mutationFn: () => fetchSummary(user?.token, true),
    onSuccess: (data) => {
      queryClient.setQueryData(["profitSharingReconciliationSummary"], data);
      message.success(i18n.t("admin.profitSharingReconciliation.balanceRefreshed"));
    },
    onError: (err) => base.handleApiError(API, err),
  });

  const orderActionMutation = useMutation({
    mutationFn: ({ orderId, action }) => postAction(user?.token, orderId, action),
    onSuccess: () => {
      message.success(i18n.t("admin.profitSharingReconciliation.actionSuccess"));
      queryClient.invalidateQueries(["profitSharingReconciliationSummary"]);
      queryClient.invalidateQueries(["profitSharingReconciliationOrders"]);
    },
    onError: (err) => base.handleApiError(API, err),
  });

  const bulkSyncMutation = useMutation({
    mutationFn: (syncScope) => postBulkSync(user?.token, syncScope),
    onSuccess: (data) => {
      setBulkSyncResult(data);
      if (data.failedCount > 0) {
        message.warning(
          i18n.t("admin.profitSharingReconciliation.bulkSync.partialSuccess", {
            success: data.successCount,
            total: data.total,
          })
        );
      } else {
        message.success(
          i18n.t("admin.profitSharingReconciliation.bulkSync.allSuccess", {
            total: data.total,
          })
        );
      }
      queryClient.invalidateQueries(["profitSharingReconciliationSummary"]);
      queryClient.invalidateQueries(["profitSharingReconciliationOrders"]);
    },
    onError: (err) => base.handleApiError(API, err),
  });

  const bulkFinishMutation = useMutation({
    mutationFn: (finishScope) => postBulkFinish(user?.token, finishScope),
    onSuccess: (data) => {
      setBulkFinishResult(data);
      if (data.failedCount > 0) {
        message.warning(
          i18n.t("admin.profitSharingReconciliation.bulkFinish.partialSuccess", {
            success: data.successCount,
            total: data.total,
          })
        );
      } else {
        message.success(
          i18n.t("admin.profitSharingReconciliation.bulkFinish.allSuccess", {
            total: data.total,
          })
        );
      }
      queryClient.invalidateQueries(["profitSharingReconciliationSummary"]);
      queryClient.invalidateQueries(["profitSharingReconciliationOrders"]);
    },
    onError: (err) => base.handleApiError(API, err),
  });

  const bulkActionScope =
    scope === "pending" || scope === "flagged" ? scope : "pending";
  const bulkActionOrderCount =
    bulkActionScope === "pending"
      ? summary?.dbTotals?.countPending ?? 0
      : ordersData?.pagination?.total ?? summary?.dbTotals?.countPending ?? 0;
  const bulkActionBusy =
    bulkSyncMutation.isLoading || bulkFinishMutation.isLoading;

  const handleBulkSync = () => {
    if (appliedOrderId) {
      message.warning(i18n.t("admin.profitSharingReconciliation.bulkSync.clearOrderFilter"));
      return;
    }
    if (bulkActionOrderCount <= 0) {
      message.info(i18n.t("admin.profitSharingReconciliation.bulkSync.noOrders"));
      return;
    }

    Modal.confirm({
      title: i18n.t("admin.profitSharingReconciliation.bulkSync.confirmTitle"),
      content: i18n.t("admin.profitSharingReconciliation.bulkSync.confirmDescription", {
        count: bulkActionOrderCount,
        scope: i18n.t(`admin.profitSharingReconciliation.scopes.${bulkActionScope}`),
      }),
      okText: i18n.t("admin.profitSharingReconciliation.bulkSync.run"),
      cancelText: i18n.t("common.cancelText"),
      onOk: () => bulkSyncMutation.mutateAsync(bulkActionScope),
    });
  };

  const handleBulkFinish = () => {
    if (appliedOrderId) {
      message.warning(
        i18n.t("admin.profitSharingReconciliation.bulkFinish.clearOrderFilter")
      );
      return;
    }
    if (bulkActionOrderCount <= 0) {
      message.info(i18n.t("admin.profitSharingReconciliation.bulkFinish.noOrders"));
      return;
    }

    Modal.confirm({
      title: i18n.t("admin.profitSharingReconciliation.bulkFinish.confirmTitle"),
      content: i18n.t(
        "admin.profitSharingReconciliation.bulkFinish.confirmDescription",
        {
          count: bulkActionOrderCount,
          scope: i18n.t(
            `admin.profitSharingReconciliation.scopes.${bulkActionScope}`
          ),
        }
      ),
      okText: i18n.t("admin.profitSharingReconciliation.bulkFinish.run"),
      cancelText: i18n.t("common.cancelText"),
      onOk: () => bulkFinishMutation.mutateAsync(bulkActionScope),
    });
  };

  const handleSearchOrder = () => {
    setAppliedOrderId(orderIdFilter.trim());
    setPage(1);
  };

  const handleSwitchStaleZeroScope = useCallback(() => {
    setScope("stale_zero");
    setPage(1);
    message.info(i18n.t("admin.profitSharingReconciliation.playbook.switchedStaleZeroScope"));
  }, []);

  const columns = useMemo(
    () => [
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.orderId"),
        dataIndex: "orderId",
        width: 100,
        render: (id) => (
          <Link to={`/main/admin/order-debug?orderId=${id}`} target="_blank">
            {id}
          </Link>
        ),
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.createdAt"),
        dataIndex: "createdAt",
        width: 170,
        render: (v) => (v ? new Date(v).toLocaleString() : "—"),
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.total"),
        dataIndex: "total",
        width: 110,
        render: (v) => fenToYuan(v),
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.unfinished"),
        dataIndex: "profitUnfinishedAmount",
        width: 120,
        render: (v) => fenToYuan(v),
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.status"),
        dataIndex: "statusLabel",
        width: 120,
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.refund"),
        dataIndex: "refund",
        width: 100,
        render: (refund) => <RefundCell refund={refund} />,
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.issues"),
        dataIndex: "issues",
        render: (issues) => <IssueTags issues={issues} />,
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.sharingRounds"),
        dataIndex: "sharingRounds",
        render: (rounds) => <SharingRoundsCell rounds={rounds} />,
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.recommended"),
        dataIndex: "recommendedAction",
        width: 140,
        render: (action) => {
          const key = ACTION_LABELS[action] || action;
          return i18n.t(key) !== key ? i18n.t(key) : action;
        },
      },
      {
        title: i18n.t("admin.profitSharingReconciliation.columns.actions"),
        key: "actions",
        width: 200,
        fixed: "right",
        render: (_, row) => (
          <Space size="small" wrap>
            <Button
              size="small"
              loading={orderActionMutation.isLoading}
              onClick={() =>
                orderActionMutation.mutate({ orderId: row.orderId, action: "sync" })
              }
            >
              {row.recommendedAction === "sync_refunded_cleanup"
                ? i18n.t("admin.profitSharingReconciliation.syncRefundedCleanup")
                : i18n.t("admin.profitSharingReconciliation.syncOrder")}
            </Button>
            {!row.refund?.isRefunded && (
              <Tooltip
                title={
                  canRequestFinish(row)
                    ? undefined
                    : i18n.t("admin.profitSharingReconciliation.finishRequiresCompleted")
                }
              >
                <Button
                  size="small"
                  type="primary"
                  disabled={!canRequestFinish(row)}
                  loading={orderActionMutation.isLoading}
                  onClick={() =>
                    orderActionMutation.mutate({ orderId: row.orderId, action: "finish" })
                  }
                >
                  {i18n.t("admin.profitSharingReconciliation.requestFinish")}
                </Button>
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [orderActionMutation.isLoading]
  );

  const gap = summary?.reconciliation?.pendingGap;
  const realPending = summary?.reconciliation?.realPendingAmount;
  const residualGap = summary?.reconciliation?.residualGap;
  const gapAlert =
    realPending !== null && realPending !== undefined && Math.abs(realPending) > 100 ? (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={i18n.t("admin.profitSharingReconciliation.gapTitle")}
        description={i18n.t("admin.profitSharingReconciliation.gapDescription", {
          realPending: fenToYuan(realPending),
          pending: fenToYuan(summary?.balance?.pendingAmount),
          inProgressDb: fenToYuan(summary?.dbTotals?.sumInProgressUnfinished),
          completedDb: fenToYuan(summary?.dbTotals?.sumUnfinishedAmount),
          residualGap: fenToYuan(residualGap),
          legacyGap: fenToYuan(gap),
        })}
      />
    ) : null;

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={i18n.t("admin.profitSharingReconciliation.title")}
        extra={
          <Space>
            <Button
              onClick={handleRefresh}
              loading={summaryFetching || ordersFetching}
            >
              {i18n.t("common.refresh")}
            </Button>
            <Button
              type="primary"
              loading={refreshBalanceMutation.isLoading}
              onClick={() => refreshBalanceMutation.mutate()}
            >
              {i18n.t("admin.profitSharingReconciliation.refreshWechatBalance")}
            </Button>
          </Space>
        }
      >
        <Paragraph type="secondary">
          {i18n.t("admin.profitSharingReconciliation.description")}
        </Paragraph>
        {merchantLabel && (
          <Paragraph>
            <Text strong>{i18n.t("admin.profitSharingReconciliation.currentMerchant")}: </Text>
            {merchantLabel}
          </Paragraph>
        )}

        {summaryLoading && !summary ? (
          <Spin />
        ) : (
          summary && <SummaryStatistics summary={summary} />
        )}

        {gapAlert}

        {summary && (
          <PendingGapPlaybook
            summary={summary}
            merchantId={user?.merchant?.id}
            onSwitchStaleZeroScope={handleSwitchStaleZeroScope}
          />
        )}

        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            value={scope}
            style={{ width: 220 }}
            onChange={(v) => {
              setScope(v);
              setPage(1);
            }}
            options={SCOPE_OPTIONS.map((o) => ({
              value: o.value,
              label: i18n.t(`admin.profitSharingReconciliation.${o.labelKey}`),
            }))}
          />
          <Input
            placeholder={i18n.t("admin.profitSharingReconciliation.orderIdFilter")}
            value={orderIdFilter}
            onChange={(e) => setOrderIdFilter(e.target.value)}
            onPressEnter={handleSearchOrder}
            style={{ width: 160 }}
          />
          <Button onClick={handleSearchOrder}>{i18n.t("common.search")}</Button>
          <Tooltip
            title={
              appliedOrderId
                ? i18n.t("admin.profitSharingReconciliation.bulkSync.clearOrderFilter")
                : i18n.t("admin.profitSharingReconciliation.bulkSync.hint")
            }
          >
            <Button
              type="primary"
              loading={bulkSyncMutation.isLoading}
              disabled={appliedOrderId || bulkActionOrderCount <= 0 || bulkActionBusy}
              onClick={handleBulkSync}
            >
              {i18n.t("admin.profitSharingReconciliation.bulkSync.run")}
              {bulkActionOrderCount > 0 ? ` (${bulkActionOrderCount})` : ""}
            </Button>
          </Tooltip>
          <Tooltip
            title={
              appliedOrderId
                ? i18n.t(
                    "admin.profitSharingReconciliation.bulkFinish.clearOrderFilter"
                  )
                : i18n.t("admin.profitSharingReconciliation.bulkFinish.hint")
            }
          >
            <Button
              loading={bulkFinishMutation.isLoading}
              disabled={appliedOrderId || bulkActionOrderCount <= 0 || bulkActionBusy}
              onClick={handleBulkFinish}
            >
              {i18n.t("admin.profitSharingReconciliation.bulkFinish.run")}
              {bulkActionOrderCount > 0 ? ` (${bulkActionOrderCount})` : ""}
            </Button>
          </Tooltip>
        </Space>

        <Table
          rowKey="orderId"
          loading={ordersLoading || ordersFetching}
          columns={columns}
          dataSource={ordersData?.rows || []}
          scroll={{ x: 1200 }}
          pagination={{
            current: ordersData?.pagination?.page || page,
            pageSize: ordersData?.pagination?.perPage || 20,
            total: ordersData?.pagination?.total || 0,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
          }}
        />
      </Card>

      <BulkActionResultsModal
        open={bulkSyncResult != null}
        data={bulkSyncResult}
        onClose={() => setBulkSyncResult(null)}
        i18nKey="bulkSync"
      />
      <BulkActionResultsModal
        open={bulkFinishResult != null}
        data={bulkFinishResult}
        onClose={() => setBulkFinishResult(null)}
        i18nKey="bulkFinish"
      />
    </div>
  );
};

export default ProfitSharingReconciliation;
